/**
 * Карта роутов. URL повторяют legacy — старые ссылки и закладки жителей работают.
 *
 * Публичная часть и ЛК: пути равны modules.module (Modules::menu()) —
 * '', news, services, contacts, login | managers, docs, cameras, information,
 * votes, bulletinboard, payment.
 *
 * Админка: пути внутри /admin равны modules.module (Modules::adminMenu()) —
 * AdminLayout строит ссылки как /admin/<module>?group=<group>. Пункт «Главная»
 * имеет пустой module, поэтому ему соответствует index-роут.
 * Меню (admin_value = 1): '', news, information, services, contacts, managers,
 * docs, slider, votes, bulletinboard, areas, users_transactions, custom_invoices,
 * snt_tarifs, snt_monthes_stchets, counters, mailing, email_info,
 * statistic_log. Верхнее меню суперадмина: settings, modules, error_404, users.
 * (SMS-рассылка tel_msgs убрана — канал отключён.)
 *
 * Разделы вне меню, но доступные по прямому URL (как в legacy Admin.php):
 * areas/{id} (legacy /admin/area?id=N), snt_users, bankparse, payment_statistics.
 */

import { Navigate, Route, Routes } from 'react-router-dom'

import { Layout } from '@/components/Layout'
import { RequireAdmin, RequireUser } from '@/components/Guards'
import { useSession } from '@/session/SessionProvider'

import { AdminLayout } from '@/pages/admin/AdminLayout'
import { AdminActionLogPage } from '@/pages/admin/AdminActionLogPage'
import { AdminAreaPage } from '@/pages/admin/AdminAreaPage'
import { AdminAreasPage } from '@/pages/admin/AdminAreasPage'
import { AdminBankParsePage } from '@/pages/admin/AdminBankParsePage'
import { AdminBulletinBoardPage } from '@/pages/admin/AdminBulletinBoardPage'
import { AdminCountersPage } from '@/pages/admin/AdminCountersPage'
import { AdminCustomInvoicesPage } from '@/pages/admin/AdminCustomInvoicesPage'
import { AdminDirectoriesPage } from '@/pages/admin/AdminDirectoriesPage'
import { AdminDocsPage } from '@/pages/admin/AdminDocsPage'
import { AdminInformationPage } from '@/pages/admin/AdminInformationPage'
import { AdminInvoicesPage } from '@/pages/admin/AdminInvoicesPage'
import { AdminLoginPage } from '@/pages/admin/AdminLoginPage'
import {
  MaxMailingPage,
  PushMailingPage,
  TelegramMailingPage,
} from '@/pages/admin/AdminChannelMailingPage'
import { AdminMailingPage } from '@/pages/admin/AdminMailingPage'
import { AdminMainPage } from '@/pages/admin/AdminMainPage'
import { AdminModulesPage } from '@/pages/admin/AdminModulesPage'
import { AdminNewsPage } from '@/pages/admin/AdminNewsPage'
import { AdminPagesPage } from '@/pages/admin/AdminPagesPage'
import { AdminPaymentStatsPage } from '@/pages/admin/AdminPaymentStatsPage'
import { AdminSettingsPage } from '@/pages/admin/AdminSettingsPage'
import { AdminSliderPage } from '@/pages/admin/AdminSliderPage'
import { AdminSntUsersPage } from '@/pages/admin/AdminSntUsersPage'
import { AdminTarifsPage } from '@/pages/admin/AdminTarifsPage'
import { AdminTransactionsPage } from '@/pages/admin/AdminTransactionsPage'
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage'
import { AdminVotesPage } from '@/pages/admin/AdminVotesPage'
import { BulletinBoardPage } from '@/pages/BulletinBoardPage'
import { CamerasPage } from '@/pages/CamerasPage'
import { ContactsPage } from '@/pages/ContactsPage'
import { DocsPage } from '@/pages/DocsPage'
import { HomePage } from '@/pages/HomePage'
import { InformationPage } from '@/pages/InformationPage'
import { LkPage } from '@/pages/LkPage'
import { LoginPage } from '@/pages/LoginPage'
import { ManagersPage } from '@/pages/ManagersPage'
import { NewsPage } from '@/pages/NewsPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { PaymentPage } from '@/pages/PaymentPage'
import { ServicesPage } from '@/pages/ServicesPage'
import { VotePage } from '@/pages/VotePage'
import { VotesPage } from '@/pages/VotesPage'

export function App() {
  const { loading } = useSession()

  // Сессия ещё не прочитана — legacy рендерил страницу уже с готовой сессией,
  // поэтому здесь просто ничего не показываем (без своей вёрстки-«лоадера»).
  if (loading) return null

  return (
    <Routes>
      {/* Админка — свой layout (admin.tpl), без публичной шапки */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route
        path="/admin"
        element={
          <RequireAdmin>
            <AdminLayout />
          </RequireAdmin>
        }
      >
        {/* group=1 «Управление сайтом» */}
        <Route index element={<AdminMainPage />} />
        <Route path="news" element={<AdminNewsPage />} />
        <Route path="services" element={<AdminDirectoriesPage />} />
        <Route path="contacts" element={<AdminDirectoriesPage />} />
        <Route path="slider" element={<AdminSliderPage />} />

        {/* group=4 «Управление ЛК» */}
        <Route path="information" element={<AdminInformationPage />} />
        <Route path="managers" element={<AdminDirectoriesPage />} />
        <Route path="docs" element={<AdminDocsPage />} />
        <Route path="bulletinboard" element={<AdminBulletinBoardPage />} />

        {/* group=5 «Администрирование» */}
        <Route path="areas" element={<AdminAreasPage />} />
        <Route path="areas/:id" element={<AdminAreaPage />} />
        <Route path="users_transactions" element={<AdminTransactionsPage />} />
        <Route path="votes" element={<AdminVotesPage />} />
        <Route path="custom_invoices" element={<AdminCustomInvoicesPage />} />

        {/* group=2 «Электроэнергия» */}
        <Route path="snt_tarifs" element={<AdminTarifsPage />} />
        <Route path="snt_monthes_stchets" element={<AdminInvoicesPage />} />
        <Route path="counters" element={<AdminCountersPage />} />

        {/* group=3 «Рассылки». SMS-рассылка (tel_msgs / tel_msgs_contacts) убрана: канал отключён.
            Telegram/Push/Max — отдельные страницы «по аналогии с Email» (пункты меню — в
            новой админке, backend Modules::adminMenu). */}
        <Route path="mailing" element={<AdminMailingPage />} />
        <Route path="email_info" element={<AdminMailingPage />} />
        <Route path="telegram_msgs" element={<TelegramMailingPage />} />
        <Route path="push_msgs" element={<PushMailingPage />} />
        <Route path="max_msgs" element={<MaxMailingPage />} />
        <Route path="statistic_log" element={<AdminActionLogPage />} />

        {/* Вне меню, но открывается по прямой ссылке — как в legacy Admin.php */}
        <Route path="snt_users" element={<AdminSntUsersPage />} />
        <Route path="bankparse" element={<AdminBankParsePage />} />
        <Route path="payment_statistics" element={<AdminPaymentStatsPage />} />

        {/* Верхнее меню суперадмина */}
        <Route path="settings" element={<AdminSettingsPage />} />
        <Route path="modules" element={<AdminModulesPage />} />
        <Route path="error_404" element={<AdminPagesPage />} />
        <Route path="users" element={<AdminUsersPage />} />

        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>

      {/* Публичная часть и ЛК */}
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="news" element={<NewsPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="services" element={<ServicesPage />} />

        <Route element={<RequireUser />}>
          <Route path="lk" element={<LkPage />} />
          <Route path="payment" element={<PaymentPage />} />
          <Route path="information" element={<InformationPage />} />
          <Route path="docs" element={<DocsPage />} />
          <Route path="managers" element={<ManagersPage />} />
          <Route path="cameras" element={<CamerasPage />} />
          <Route path="bulletinboard" element={<BulletinBoardPage />} />
          <Route path="votes" element={<VotesPage />} />
          <Route path="votes/:id" element={<VotePage />} />
        </Route>

        <Route path="404" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Route>
    </Routes>
  )
}
